import { API_BASE_URL, customFetch } from './api'
import type {
    Publication,
    Category,
    Project,
    Website,
    ApiResponse,
    // NestedDataResponse, // TODO: Unused for now
    ProjectReport,
    PendudukStatistics,
    StatistikPendidikan,
    StatistikPekerjaan,
    StatistikAgama,
    StatistikKesehatan,
    MenuLocation,
    Menu,
    MenuItem,
    Tag,
    Desa,
    // StrukturPemerintahan, // TODO: Unused for now
    // DesaInfo, // TODO: Unused for now
    StrukturPemerintahanDetail,
    ChartData,
    DesilData,
    DesilListResult,
    DesilStatistics,
    DesilFilter,
    DesilFilteredResult,
    BukuTamuCreateInput,
    BukuTamuEntry,
    PejabatDropdownItem,
} from './types'

// Menu API
export const menuApi = {
    async getMenuLocations(): Promise<ApiResponse<MenuLocation[]>> {
        const response = await customFetch(`${API_BASE_URL}/menu/locations`)
        return response.json()
    },

    async getMenus(locationId?: string): Promise<ApiResponse<Menu[]>> {
        const searchParams = new URLSearchParams()
        if (locationId) searchParams.set('locationId', locationId)

        const response = await customFetch(`${API_BASE_URL}/menu?${searchParams}`)
        return response.json()
    },

    async getMenuById(id: string): Promise<ApiResponse<Menu>> {
        const response = await customFetch(`${API_BASE_URL}/menu/${id}`)
        return response.json()
    },

    async getMenuItems(menuId: string): Promise<ApiResponse<MenuItem[]>> {
        const response = await customFetch(`${API_BASE_URL}/menu/${menuId}/items`)
        return response.json()
    },

    async getMenusByLocationType(type: string): Promise<ApiResponse<Menu[]>> {
        const response = await customFetch(`${API_BASE_URL}/menu/by-location-type/${type}`)
        return response.json()
    }
}

// Publication API
export const publicationApi = {
    async getPublications(params: {
        type?: 'NEWS' | 'PAGE' | 'REPORT';
        categoryId?: string;
        search?: string;
        page?: string | number;
        limit?: string | number;
        status?: 'PUBLISHED';
    }): Promise<ApiResponse<Publication[]>> {
        const searchParams = new URLSearchParams()

        // Only add published content for public website
        searchParams.set('status', 'PUBLISHED')

        if (params.type) searchParams.set('type', params.type)
        if (params.categoryId) searchParams.set('categoryId', params.categoryId)
        if (params.search) searchParams.set('search', params.search)
        if (params.page) {
            const pageNum = typeof params.page === 'string' ? parseInt(params.page) : params.page
            if (!isNaN(pageNum) && pageNum > 0) {
                searchParams.set('page', pageNum.toString())
            }
        }
        if (params.limit) {
            const limitNum = typeof params.limit === 'string' ? parseInt(params.limit) : params.limit
            if (!isNaN(limitNum) && limitNum > 0) {
                searchParams.set('limit', limitNum.toString())
            }
        }

        const response = await customFetch(`${API_BASE_URL}/pub?${searchParams}`)
        const result = await response.json()
        return {
            data: result.data?.data || [],
            status: 'success'
        }
    },

    async getPublicationBySlug(slug: string): Promise<ApiResponse<Publication>> {
        const response = await customFetch(`${API_BASE_URL}/pub/by-slug/${slug}`)
        return response.json()
    },

    async getRecentPublications(params: {
        type?: 'NEWS' | 'PAGE' | 'REPORT';
        limit?: number;
    } = {}): Promise<ApiResponse<Publication[]>> {
        const searchParams = new URLSearchParams()
        searchParams.set('status', 'PUBLISHED')
        searchParams.set('sortBy', 'publishedAt')
        searchParams.set('sortOrder', 'desc')

        if (params.type) searchParams.set('type', params.type)
        if (params.limit) searchParams.set('limit', params.limit.toString())

        const response = await customFetch(`${API_BASE_URL}/pub?${searchParams}`)
        const result = await response.json()
        return {
            data: result.data?.data || [],
            status: 'success'
        }
    },

    async getPopularPublications(params: {
        type?: 'NEWS' | 'PAGE' | 'REPORT';
        limit?: number;
    } = {}): Promise<ApiResponse<Publication[]>> {
        const searchParams = new URLSearchParams()
        searchParams.set('status', 'PUBLISHED')
        searchParams.set('sortBy', 'createdAt')
        searchParams.set('sortOrder', 'desc')

        if (params.type) searchParams.set('type', params.type)
        if (params.limit) searchParams.set('limit', params.limit.toString())

        const response = await customFetch(`${API_BASE_URL}/pub?${searchParams}`)
        const result = await response.json()
        return {
            data: result.data?.data || [],
            status: 'success'
        }
    },

    async getFeaturedPublications(params: {
        type?: 'NEWS' | 'PAGE' | 'REPORT';
        limit?: number;
    } = {}): Promise<ApiResponse<Publication[]>> {
        const searchParams = new URLSearchParams()
        searchParams.set('status', 'PUBLISHED')
        searchParams.set('isFeatured', 'true')
        searchParams.set('sortBy', 'publishedAt')
        searchParams.set('sortOrder', 'desc')

        if (params.type) searchParams.set('type', params.type)
        if (params.limit) searchParams.set('limit', params.limit.toString())

        const response = await customFetch(`${API_BASE_URL}/pub?${searchParams}`)
        const result = await response.json()
        return {
            data: result.data?.data || [],
            status: 'success'
        }
    }
}

// Category API
export const categoryApi = {
    async getCategories(): Promise<ApiResponse<Category[]>> {
        const response = await customFetch(`${API_BASE_URL}/pub/categories`)
        return response.json()
    },

    async getCategoryBySlug(slug: string): Promise<ApiResponse<Category>> {
        const response = await customFetch(`${API_BASE_URL}/pub/categories/slug/${slug}`)
        return response.json()
    }
}

// Project API
export const projectApi = {
    async getProjects(params: {
        search?: string;
        page?: string | number;
        limit?: string | number;
        status?: 'COMPLETED' | 'IN_PROGRESS' | 'PLANNING' | 'ON_HOLD';
        sortBy?: 'createdAt' | 'startDate' | 'endDate' | 'title';
        sortOrder?: 'asc' | 'desc';
    } = {}): Promise<ApiResponse<Project[]>> {
        const searchParams = new URLSearchParams()

        if (params.search) searchParams.set('search', params.search)

        // Provide default values for required fields
        const pageNum = params.page ?
            (typeof params.page === 'string' ? parseInt(params.page) : params.page) : 1
        const limitNum = params.limit ?
            (typeof params.limit === 'string' ? parseInt(params.limit) : params.limit) : 10

        // Ensure valid numbers
        if (!isNaN(pageNum) && pageNum > 0) {
            searchParams.set('page', pageNum.toString())
        } else {
            searchParams.set('page', '1')
        }

        if (!isNaN(limitNum) && limitNum > 0) {
            searchParams.set('limit', limitNum.toString())
        } else {
            searchParams.set('limit', '10')
        }

        if (params.status) searchParams.set('status', params.status)
        if (params.sortBy) searchParams.set('sortBy', params.sortBy)
        if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder)

        const response = await customFetch(`${API_BASE_URL}/project?${searchParams}`)
        const result = await response.json()
        return {
            data: result.data?.data || [],
            status: 'success',
            // @ts-ignore - Extended response untuk projects dengan pagination
            meta: result.data?.meta || null
        }
    },

    async getProjectById(id: string): Promise<ApiResponse<Project>> {
        const response = await customFetch(`${API_BASE_URL}/project/${id}`)
        return response.json()
    },

    async getProjectBySlug(slug: string): Promise<ApiResponse<Project>> {
        const response = await customFetch(`${API_BASE_URL}/project/by-slug/${slug}`)
        return response.json()
    }
}

// Project Reports API - Using dedicated project report endpoints
export const projectReportApi = {
    async getReports(params: {
        projectId?: string;
        status?: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
        type?: 'PROGRESS' | 'MILESTONE' | 'FINAL' | 'ISSUE';
        search?: string;
        page?: string | number;
        limit?: string | number;
        sortBy?: 'createdAt' | 'publishedAt' | 'reportDate' | 'title';
        sortOrder?: 'asc' | 'desc';
    } = {}): Promise<ApiResponse<ProjectReport[]>> {
        const searchParams = new URLSearchParams()

        // For public website, only show published reports
        searchParams.set('status', 'PUBLISHED')

        if (params.projectId) searchParams.set('projectId', params.projectId)
        if (params.type) searchParams.set('type', params.type)
        if (params.search) searchParams.set('search', params.search)

        // Handle pagination
        const pageNum = params.page ?
            (typeof params.page === 'string' ? parseInt(params.page) : params.page) : 1
        const limitNum = params.limit ?
            (typeof params.limit === 'string' ? parseInt(params.limit) : params.limit) : 10

        if (!isNaN(pageNum) && pageNum > 0) {
            searchParams.set('page', pageNum.toString())
        }
        if (!isNaN(limitNum) && limitNum > 0) {
            searchParams.set('limit', limitNum.toString())
        }

        // Convert page to offset for backend
        const offset = (pageNum - 1) * limitNum
        searchParams.set('offset', offset.toString())

        // Handle sorting
        if (params.sortBy) searchParams.set('sortBy', params.sortBy)
        if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder)

        const response = await customFetch(`${API_BASE_URL}/project/reports?${searchParams}`)
        const result = await response.json()
        return {
            data: result.data?.data || [],
            status: 'success',
            // @ts-ignore - Extended response untuk reports dengan meta
            meta: result.data?.meta || null
        }
    },

    async getReportBySlug(slug: string): Promise<ApiResponse<ProjectReport>> {
        const response = await customFetch(`${API_BASE_URL}/project/reports/by-slug/${slug}`)
        return response.json()
    },

    async getReportsByProject(projectId: string, params: {
        page?: string | number;
        limit?: string | number;
    } = {}): Promise<ApiResponse<ProjectReport[]>> {
        const searchParams = new URLSearchParams()
        searchParams.set('status', 'PUBLISHED')

        const pageNum = params.page ?
            (typeof params.page === 'string' ? parseInt(params.page) : params.page) : 1
        const limitNum = params.limit ?
            (typeof params.limit === 'string' ? parseInt(params.limit) : params.limit) : 10

        const offset = (pageNum - 1) * limitNum
        searchParams.set('offset', offset.toString())
        searchParams.set('limit', limitNum.toString())
        searchParams.set('sortBy', 'reportDate')
        searchParams.set('sortOrder', 'desc')

        const response = await customFetch(`${API_BASE_URL}/project/reports/by-project/${projectId}`)
        const result = await response.json()
        return {
            data: result.data?.data || [],
            status: 'success'
        }
    },

    async getRecentReports(params: {
        limit?: number;
        type?: 'PROGRESS' | 'MILESTONE' | 'FINAL' | 'ISSUE';
    } = {}): Promise<ApiResponse<ProjectReport[]>> {
        const searchParams = new URLSearchParams()
        searchParams.set('status', 'PUBLISHED')
        searchParams.set('sortBy', 'publishedAt')
        searchParams.set('sortOrder', 'desc')
        searchParams.set('offset', '0')

        if (params.limit) searchParams.set('limit', params.limit.toString())
        if (params.type) searchParams.set('type', params.type)

        const response = await customFetch(`${API_BASE_URL}/project/reports?${searchParams}`)
        const result = await response.json()
        return {
            data: result.data?.data || [],
            status: 'success'
        }
    },

    async getReportStats(): Promise<ApiResponse<{
        progress: number;
        milestone: number;
        final: number;
        issue: number;
        total: number;
    }>> {
        const response = await customFetch(`${API_BASE_URL}/project/reports/stats`)
        return response.json()
    },

    async getReportCategories(): Promise<ApiResponse<Category[]>> {
        const response = await customFetch(`${API_BASE_URL}/project/report-categories`)
        return response.json()
    },

    async getReportTags(): Promise<ApiResponse<Tag[]>> {
        const response = await customFetch(`${API_BASE_URL}/project/report-tags`)
        return response.json()
    }
}

// Website API
export const websiteApi = {
    async getWebsiteSettings(): Promise<ApiResponse<Website>> {
        const response = await customFetch(`${API_BASE_URL}/website`)
        return response.json()
    }
}

// Upload API
export const uploadApi = {
    async uploadFile(file: File): Promise<ApiResponse<{ fileUrl: string }>> {
        const formData = new FormData()
        formData.append('file', file)

        const response = await customFetch(`${API_BASE_URL}/upload`, {
            method: 'POST',
            body: formData
        })
        return response.json()
    },

    async uploadProjectImage(file: File): Promise<ApiResponse<{ fileUrl: string }>> {
        const formData = new FormData()
        formData.append('file', file)

        const response = await customFetch(`${API_BASE_URL}/upload/image/project`, {
            method: 'POST',
            body: formData
        })
        return response.json()
    }
}

// Penduduk API
export const pendudukApi = {
    async getStatistik(): Promise<ApiResponse<PendudukStatistics>> {
        const url = `${API_BASE_URL}/public/penduduk/statistik`
        const response = await customFetch(url)
        return response.json()
    }
}

// Statistics API
export const statistikApi = {
    async getPendidikan(): Promise<ApiResponse<StatistikPendidikan>> {
        const url = `${API_BASE_URL}/public/penduduk/statistik/pendidikan`
        const response = await customFetch(url)
        return response.json()
    },
    
    async getPekerjaan(): Promise<ApiResponse<StatistikPekerjaan>> {
        const url = `${API_BASE_URL}/public/penduduk/statistik/pekerjaan`
        const response = await customFetch(url)
        return response.json()
    },
    
    async getAgama(): Promise<ApiResponse<StatistikAgama>> {
        const url = `${API_BASE_URL}/public/penduduk/statistik/agama`
        const response = await customFetch(url)
        return response.json()
    }
}

// Health Statistics API
export const healthApi = {
    async getStatistikYearly(): Promise<ApiResponse<StatistikKesehatan>> {
        const url = `${API_BASE_URL}/public/health/statistik/yearly`
        const response = await customFetch(url)
        return response.json()
    }
}

// Desa Public API
export const desaPublicApi = {
    async getDesaDetail(): Promise<ApiResponse<Desa>> {
        const response = await customFetch(`${API_BASE_URL}/public/organization/detail`)
        return response.json()
    },
}

// Pemerintahan Desa Public API
export const pemerintahDesaPublicApi = {
    async getStrukturPemerintahan(): Promise<ApiResponse<StrukturPemerintahanDetail>> {
        const response = await customFetch(`${API_BASE_URL}/public/pemerintahorganisasi/struktur`)
        return response.json()
    },

    async getChartData(): Promise<ApiResponse<ChartData>> {
        const response = await customFetch(`${API_BASE_URL}/public/pemerintahorganisasi/chart`)
        return response.json()
    },
}

// Desil Public API
export const desilApi = {
    async getDesil(params?: {
        page?: number;
        limit?: number;
        tahap?: number;
        tahun?: number;
    }): Promise<ApiResponse<DesilListResult>> {
        const searchParams = new URLSearchParams()
        if (params?.page) searchParams.set('page', params.page.toString())
        if (params?.limit) searchParams.set('limit', params.limit.toString())
        if (params?.tahap) searchParams.set('tahap', params.tahap.toString())
        if (params?.tahun) searchParams.set('tahun', params.tahun.toString())

        const url = `${API_BASE_URL}/public/desil?${searchParams}`
        const response = await customFetch(url)
        return response.json()
    },

    async getLatestDesil(): Promise<ApiResponse<DesilData | null>> {
        const url = `${API_BASE_URL}/public/desil/latest`
        const response = await customFetch(url)
        return response.json()
    },

    async getStats(): Promise<ApiResponse<DesilStatistics>> {
        const url = `${API_BASE_URL}/public/desil/statistik`
        const response = await customFetch(url)
        return response.json()
    },

    async getFiltered(filters?: DesilFilter): Promise<ApiResponse<DesilFilteredResult>> {
        const searchParams = new URLSearchParams()
        if (filters?.tahap) searchParams.set('tahap', filters.tahap.toString())
        if (filters?.startYear) searchParams.set('startYear', filters.startYear.toString())
        if (filters?.endYear) searchParams.set('endYear', filters.endYear.toString())
        if (filters?.sortOrder) searchParams.set('sortOrder', filters.sortOrder)

        const url = `${API_BASE_URL}/public/desil/filtered?${searchParams}`
        const response = await customFetch(url)
        return response.json()
    }
}

// Buku Tamu (Guest Book) Public API
export const bukuTamuApi = {
    async getPejabatList(): Promise<ApiResponse<PejabatDropdownItem[]>> {
        const response = await customFetch(`${API_BASE_URL}/public/buku-tamu/pejabat`)
        return response.json()
    },

    async submit(data: BukuTamuCreateInput[]): Promise<ApiResponse<BukuTamuEntry[]>> {
        const response = await customFetch(`${API_BASE_URL}/public/buku-tamu`, {
            method: 'POST',
            body: JSON.stringify(data),
        })
        return response.json()
    },
}
